import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './QuizSelection.css';

/**
 * Parse quiz content from mellow:quiz:selection block
 * Format:
 * ```mellow:quiz:selection
 * Question text (can be multi-line)
 * ---
 * 1. Option one
 * 2. Option two
 * 3. Option three
 * ---
 * answer: 2
 * hint: Explanation text (optional)
 * ```
 */
export function parseQuizContent(content) {
  const sections = content.split('---').map(s => s.trim());
  
  const question = sections[0] || '';
  
  // Parse options (lines starting with number.)
  const options = (sections[1] || '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => /^\d+\./.test(line))
    .map(line => line.replace(/^\d+\.\s*/, '').trim());
  
  // Parse answer and hint
  let answer = 1;
  let hint = '';
  
  if (sections[2]) {
    sections[2].split('\n').forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('answer:')) {
        answer = parseInt(trimmed.replace('answer:', '').trim(), 10) || 1;
      } else if (trimmed.startsWith('hint:')) {
        hint = trimmed.replace('hint:', '').trim();
      }
    });
  }
  
  return { question, options, answer, hint };
}

/**
 * Interactive quiz component with Duolingo-style feedback
 * @param {boolean} disabled - When true, quiz is non-interactive (for shared view)
 */
export default function QuizSelection({ content, disabled = false }) {
  const { t } = useTranslation('chat');
  const { question, options, answer, hint } = parseQuizContent(content);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [state, setState] = useState('idle'); // idle | correct | wrong | revealed
  const [attempts, setAttempts] = useState(0);
  const [shakingIndex, setShakingIndex] = useState(null);

  const correctIndex = answer - 1; // Convert 1-based to 0-based

  const handleOptionClick = (index) => {
    // if (disabled) return; // Disabled in shared view
    // Currrently Quiz won't submit any real data, so no need to disable yet, keep for future need
    if (state === 'correct' || state === 'revealed') return;
    
    setSelectedIndex(index);
    
    if (index === correctIndex) {
      // Correct!
      setState('correct');
    } else {
      // Wrong - shake and reset
      setState('wrong');
      setShakingIndex(index);
      setAttempts(prev => prev + 1);
      
      // After 3 attempts, reveal answer
      if (attempts >= 2) {
        setTimeout(() => {
          setState('revealed');
          setShakingIndex(null);
        }, 400);
      } else {
        // Reset after shake animation
        setTimeout(() => {
          setState('idle');
          setSelectedIndex(null);
          setShakingIndex(null);
        }, 400);
      }
    }
  };
  
  const handleReset = () => {
    setState('idle');
    setSelectedIndex(null);
    setAttempts(0);
    setShakingIndex(null);
  };
  
  const getOptionClassName = (index) => {
    const classes = ['quiz-option'];
    
    if (state === 'correct' && index === correctIndex) {
      classes.push('correct');
    } else if (state === 'revealed' && index === correctIndex) {
      classes.push('revealed');
    } else if (shakingIndex === index) {
      classes.push('wrong', 'shake');
    } else if (state === 'correct' || state === 'revealed') {
      classes.push('disabled');
    }
    
    return classes.join(' ');
  };
  
  // When disabled, show quiz in read-only state
  const isDisabledOrComplete = disabled || state === 'correct' || state === 'revealed';

  return (
    <div className={`quiz-selection ${disabled ? 'disabled' : ''}`}>
      <div className="quiz-question">{question}</div>

      <div className="quiz-options">
        {options.map((option, index) => (
          <button
            key={index}
            className={getOptionClassName(index)}
            onClick={() => handleOptionClick(index)}
            disabled={isDisabledOrComplete}
          >
            <span className="quiz-option-number">{index + 1}</span>
            <span className="quiz-option-text">{option}</span>
          </button>
        ))}
      </div>

      {(state === 'correct' || state === 'revealed') && hint && (
        <div className="quiz-hint">
          <span className="quiz-hint-icon">💡</span>
          {hint}
        </div>
      )}

      {state === 'revealed' && (
        <div className="quiz-revealed-message">
          {t('quiz.correctAnswer', { answer })}
        </div>
      )}

      {(state === 'correct' || state === 'revealed') && !disabled && (
        <button className="quiz-reset-btn" onClick={handleReset}>
          {t('quiz.tryAgain')}
        </button>
      )}
    </div>
  );
}

